import React, { useRef, useState } from 'react';
import { applyQortexSync, previewQortexSync } from '../../api/client';
import { QortexSyncReport } from '../../api/models';
import { SceneState, useSceneStore } from '../../state/sceneStore';

interface QortexSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'file' | 'preview';

const SENSOR_TYPE_OPTIONS = ['HD', 'Dome', 'Rotating', 'MEMS'] as const;

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.75)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const panelStyle: React.CSSProperties = {
  background: '#1e293b',
  color: 'white',
  borderRadius: '8px',
  border: '1px solid #334155',
  padding: '20px',
  maxWidth: '640px',
  width: '92%',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
};

const scrollBodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
  marginBottom: '12px',
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: '#334155',
};

const selectStyle: React.CSSProperties = {
  background: '#334155',
  color: 'white',
  border: '1px solid #475569',
  borderRadius: '4px',
  padding: '6px 8px',
  fontSize: '13px',
};

const renderNameList = (label: string, names: string[]) => {
  if (names.length === 0) {
    return null;
  }
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
        {label} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({names.length})</span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#cbd5e1',
          lineHeight: 1.5,
          maxHeight: '72px',
          overflowY: 'auto',
          padding: '6px 8px',
          background: 'rgba(15, 23, 42, 0.5)',
          borderRadius: '4px',
        }}
      >
        {names.join(', ')}
      </div>
    </div>
  );
};

const QortexSyncModal: React.FC<QortexSyncModalProps> = ({ isOpen, onClose }) => {
  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const updateCurrentProject = useSceneStore((state: SceneState) => state.updateCurrentProject);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<QortexSyncReport | null>(null);
  const [sensorTypeOverrides, setSensorTypeOverrides] = useState<Record<string, string>>({});
  const [bulkSensorType, setBulkSensorType] = useState<string>('HD');
  const [showIndividualTypes, setShowIndividualTypes] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const reset = () => {
    setStep('file');
    setSelectedFile(null);
    setPreview(null);
    setSensorTypeOverrides({});
    setBulkSensorType('HD');
    setShowIndividualTypes(false);
    setErrorMessage(null);
    setIsLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const applyBulkSensorType = (sensorType: string) => {
    if (!preview) {
      return;
    }
    setBulkSensorType(sensorType);
    const overrides: Record<string, string> = {};
    preview.new_frames.forEach((frame) => {
      overrides[frame.frame_id] = sensorType;
    });
    setSensorTypeOverrides(overrides);
  };

  const handleFileSelected = async (file: File) => {
    if (!currentProject) {
      setErrorMessage('No project is open.');
      return;
    }

    setSelectedFile(file);
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const report = await previewQortexSync(currentProject.id, file);
      setPreview(report);
      const defaultType = report.new_frames[0]?.sensor_type || 'HD';
      const overrides: Record<string, string> = {};
      report.new_frames.forEach((frame) => {
        overrides[frame.frame_id] = frame.sensor_type || defaultType;
      });
      setBulkSensorType(defaultType);
      setSensorTypeOverrides(overrides);
      setShowIndividualTypes(report.new_frames.length <= 6);
      setStep('preview');
    } catch {
      setErrorMessage('Failed to preview sync. Check that the file is valid Q-TRACK settings XML.');
      setSelectedFile(null);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleApply = async () => {
    if (!currentProject || !selectedFile) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    try {
      const result = await applyQortexSync(currentProject.id, selectedFile, sensorTypeOverrides);
      updateCurrentProject(result.project);
      handleClose();
    } catch {
      setErrorMessage('Failed to apply sync. Check that sensor names match frame IDs in the XML.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div style={overlayStyle} data-testid="qortex-sync-modal">
      <div style={panelStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Sync with Q-TRACK</h3>

        <div style={scrollBodyStyle}>
          {step === 'file' && (
            <>
              <p style={{ fontSize: '14px', color: '#cbd5e1', lineHeight: 1.5, marginTop: 0 }}>
                Select a Q-TRACK Settings XML. Sensors are replaced with sensors from the file.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml,application/xml,text/xml"
                data-testid="qortex-sync-file-input"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    void handleFileSelected(file);
                  }
                }}
              />
              {isLoading && <p style={{ color: '#94a3b8' }}>Analyzing file...</p>}
            </>
          )}

          {step === 'preview' && preview && (
            <>
              <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5, marginTop: 0 }}>
                Review before applying. PCD/aligned status is kept only when poses match within 1 mm / 0.1 deg.
              </p>
              <div
                style={{
                  background: '#0f172a',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '13px',
                  marginBottom: '16px',
                }}
                data-testid="qortex-sync-preview"
              >
                {renderNameList('Added', preview.added)}
                {renderNameList('Removed', preview.removed)}
                {renderNameList('Kept aligned', preview.kept_aligned)}
                {renderNameList('Pose changed', preview.refreshed)}
                {preview.added.length === 0
                  && preview.removed.length === 0
                  && preview.refreshed.length === 0
                  && preview.kept_aligned.length > 0 && (
                    <p style={{ margin: 0, color: '#10b981' }}>All sensors match Q-TRACK within tolerance.</p>
                )}
              </div>

              {preview.new_frames.length > 0 && (
                <div
                  style={{
                    background: '#0f172a',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '4px',
                  }}
                  data-testid="qortex-sync-type-section"
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: showIndividualTypes ? '12px' : 0,
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>
                      Sensor type for new sensors ({preview.new_frames.length})
                    </span>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        color: '#cbd5e1',
                      }}
                    >
                      Set all to
                      <select
                        value={bulkSensorType}
                        onChange={(event) => applyBulkSensorType(event.target.value)}
                        style={{ ...selectStyle, minWidth: '110px' }}
                        data-testid="qortex-sync-type-all"
                      >
                        {SENSOR_TYPE_OPTIONS.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {preview.new_frames.length > 6 && (
                    <button
                      type="button"
                      style={{
                        ...secondaryButtonStyle,
                        width: '100%',
                        marginTop: '10px',
                        fontSize: '12px',
                      }}
                      data-testid="qortex-sync-toggle-individual-types"
                      onClick={() => setShowIndividualTypes((current) => !current)}
                    >
                      {showIndividualTypes ? 'Hide individual sensors' : 'Customize individual sensors'}
                    </button>
                  )}

                  {showIndividualTypes && (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: '8px',
                        marginTop: '12px',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        paddingRight: '4px',
                      }}
                      data-testid="qortex-sync-type-list"
                    >
                      {preview.new_frames.map((frame) => (
                        <label
                          key={frame.frame_id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                          }}
                        >
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: '#cbd5e1',
                            }}
                            title={frame.frame_id}
                          >
                            {frame.frame_id}
                          </span>
                          <select
                            value={sensorTypeOverrides[frame.frame_id] || bulkSensorType}
                            onChange={(event) => {
                              const value = event.target.value;
                              setSensorTypeOverrides((current) => ({
                                ...current,
                                [frame.frame_id]: value,
                              }));
                            }}
                            style={{ ...selectStyle, width: '96px', flexShrink: 0 }}
                            data-testid={`qortex-sync-type-${frame.frame_id}`}
                          >
                            {SENSOR_TYPE_OPTIONS.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {errorMessage && (
            <p style={{ fontSize: '13px', color: '#fca5a5', marginBottom: 0 }} data-testid="qortex-sync-error">
              {errorMessage}
            </p>
          )}
        </div>

        {step === 'preview' && preview && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button
              type="button"
              style={{ ...primaryButtonStyle, flex: 1 }}
              data-testid="qortex-sync-confirm"
              disabled={isLoading}
              onClick={() => void handleApply()}
            >
              {isLoading ? 'Applying...' : 'Apply sync'}
            </button>
            <button
              type="button"
              style={{ ...secondaryButtonStyle, flex: 1 }}
              onClick={() => {
                setStep('file');
                setPreview(null);
                setSelectedFile(null);
              }}
              disabled={isLoading}
            >
              Back
            </button>
          </div>
        )}

        <button
          type="button"
          style={{ ...secondaryButtonStyle, width: '100%' }}
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default QortexSyncModal;
